export function generateSmsPassword(): { formatted: string; digits: string } {
  const groups = Array.from({ length: 3 }, () => {
    const value = crypto.getRandomValues(new Uint32Array(1))[0] % 1000;
    return String(value).padStart(3, '0');
  });

  const digits = groups.join('');
  return {
    formatted: `${groups[0]}-${groups[1]}-${groups[2]}`,
    digits,
  };
}

const DEFAULT_COUNTRY_CODE = '31';

/** Normalize a phone number to E.164, defaulting to NL (+31) when no country code is given. */
export function normalizeToE164(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }

  let digits: string;
  if (trimmed.startsWith('+')) {
    digits = trimmed.slice(1).replace(/\D/g, '');
  } else {
    digits = trimmed.replace(/\D/g, '');
    if (digits.startsWith('00')) {
      digits = digits.slice(2);
    } else if (digits.startsWith('0')) {
      digits = DEFAULT_COUNTRY_CODE + digits.slice(1);
    } else if (!digits.startsWith(DEFAULT_COUNTRY_CODE)) {
      digits = DEFAULT_COUNTRY_CODE + digits;
    }
  }

  if (digits.length < 8 || digits.length > 15) {
    return null;
  }

  return `+${digits}`;
}

export function isValidE164Phone(phone: string): boolean {
  return normalizeToE164(phone) !== null;
}

/** Rewrite a single-line phone input to E.164 when the value can be normalized. */
export function bindPhoneInputNormalize(input: HTMLInputElement | HTMLTextAreaElement): void {
  const rewrite = () => {
    if (input instanceof HTMLTextAreaElement) {
      const lines = input.value.split(/\r?\n/);
      let changed = false;
      const next = lines.map((line) => {
        const trimmed = line.trim();
        if (!trimmed) {
          return line;
        }
        const normalized = normalizeToE164(trimmed);
        if (normalized && normalized !== line) {
          changed = true;
          return normalized;
        }
        return line;
      });
      if (changed) {
        input.value = next.join('\n');
      }
      return;
    }

    const normalized = normalizeToE164(input.value);
    if (normalized && normalized !== input.value) {
      input.value = normalized;
    }
  };

  input.addEventListener('blur', rewrite);
  input.addEventListener('paste', () => {
    queueMicrotask(rewrite);
  });
}

export function formatSmsPasswordBody(formattedPassword: string): string {
  return `ShareWeb access code: ${formattedPassword}`;
}

export interface SmsPasswordInputController {
  setVisible: (visible: boolean) => void;
  getPassword: () => string;
  isComplete: () => boolean;
  clear: () => void;
  focus: () => void;
}

export function mountSmsPasswordInput(host: HTMLElement): SmsPasswordInputController {
  host.innerHTML = `
    <span data-i18n="info.guest.smsPasswordLabel"></span>
    <div class="sms-password-input" role="group" aria-label="SMS password">
      <input type="text" inputmode="numeric" maxlength="3" autocomplete="one-time-code" aria-label="1" />
      <span aria-hidden="true">-</span>
      <input type="text" inputmode="numeric" maxlength="3" autocomplete="one-time-code" aria-label="2" />
      <span aria-hidden="true">-</span>
      <input type="text" inputmode="numeric" maxlength="3" autocomplete="one-time-code" aria-label="3" />
    </div>
  `;

  const inputs = Array.from(host.querySelectorAll('input')) as HTMLInputElement[];

  const handleInput = (index: number) => {
    const input = inputs[index];
    input.value = input.value.replace(/\D/g, '').slice(0, 3);
    if (input.value.length === 3 && index < inputs.length - 1) {
      inputs[index + 1].focus();
    }
  };

  inputs.forEach((input, index) => {
    input.addEventListener('input', () => handleInput(index));
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Backspace' && input.value.length === 0 && index > 0) {
        inputs[index - 1].focus();
      }
    });
  });

  return {
    setVisible(visible) {
      host.hidden = !visible;
    },
    getPassword() {
      return inputs.map((input) => input.value.padStart(3, '0')).join('');
    },
    isComplete() {
      return inputs.every((input) => input.value.length === 3);
    },
    clear() {
      for (const input of inputs) {
        input.value = '';
      }
    },
    focus() {
      inputs[0]?.focus();
    },
  };
}
